import ReactGA from "react-ga4";
import { clarity } from 'react-microsoft-clarity';
import { getAnonymousSessionId, getPseudonymizedUserId } from './analytics';
import { hasConsentForCategory } from './cookieConsent';

class AnalyticsManager {
  constructor() {
    this.initialized = false;
    this.gaInitialized = false;
    this.clarityInitialized = false;
  }

  /**
   * Initialize all analytics services if consent is given
   */
  async initialize() {
    if (!hasConsentForCategory('analytics')) {
      console.log('Analytics consent not given, skipping initialization');
      return;
    }

    if (this.initialized) {
      console.log('Analytics already initialized');
      return;
    }

    // Initialize Google Analytics
    this.initializeGoogleAnalytics();

    // Initialize Microsoft Clarity
    this.initializeMicrosoftClarity();

    this.initialized = true;
  }

  /**
   * Initialize Google Analytics with consent
   */
  initializeGoogleAnalytics() {
    if (this.gaInitialized || !process.env.REACT_APP_GOOGLE_ANALYTICS_KEY) {
      return;
    }

    try {
      ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_KEY || "", {
        gaOptions: {
          anonymizeIp: true, // GDPR compliance
          storage: 'none', // Don't use cookies until consent
        }
      });

      // Use anonymous session ID
      const sessionId = getAnonymousSessionId();
      ReactGA.set({
        userId: sessionId,
        dimension1: 'anonymous'
      });

      this.gaInitialized = true;
      console.log('Google Analytics initialized with consent');
    } catch (error) {
      console.error('Failed to initialize Google Analytics:', error);
    }
  }

  /**
   * Initialize Microsoft Clarity with consent
   */
  initializeMicrosoftClarity() {
    if (this.clarityInitialized || !process.env.REACT_APP_CLARITY_KEY) {
      return;
    }

    try {
      clarity.init(process.env.REACT_APP_CLARITY_KEY);
      clarity.consent();

      // Use anonymous session ID
      const anonymousSessionId = getAnonymousSessionId();
      clarity.identify(anonymousSessionId, {
        sessionType: 'anonymous',
        timestamp: new Date().toISOString()
      });

      clarity.setTag('gdpr_compliant', 'true');

      this.clarityInitialized = true;
      console.log('Microsoft Clarity initialized with consent');
    } catch (error) {
      console.error('Failed to initialize Microsoft Clarity:', error);
    }
  }

  /**
   * Track page view with consent check
   */
  trackPageView(path, title) {
    if (!hasConsentForCategory('analytics')) {
      return;
    }

    if (!this.initialized) {
      this.initialize();
    }

    if (this.gaInitialized) {
      ReactGA.send({
        hitType: "pageview",
        page: path,
        title: title
      });
    }
  }

  /**
   * Track custom event with consent check
   */
  trackEvent(category, action, label, value) {
    if (!hasConsentForCategory('analytics')) {
      return;
    }

    if (!this.initialized) {
      this.initialize();
    }

    if (this.gaInitialized) {
      ReactGA.event({
        category: category,
        action: action,
        label: label,
        value: value
      });
    }

    if (this.clarityInitialized) {
      clarity.event(action, { category, label, value });
    }
  }

  /**
   * Update user consent and reinitialize if needed
   */
  updateConsent(hasConsent) {
    if (hasConsent && !this.initialized) {
      this.initialize();
    } else if (!hasConsent && this.initialized) {
      // If consent is revoked, we need to stop tracking
      // Note: This doesn't clear already collected data
      this.initialized = false;
      this.gaInitialized = false;
      this.clarityInitialized = false;
      console.log('Analytics tracking stopped due to consent revocation');
    }
  }

  /**
   * Set user properties with pseudonymization
   */
  async setUserProperties(userId, properties = {}) {
    if (!hasConsentForCategory('analytics')) {
      return;
    }

    const pseudonymizedId = await getPseudonymizedUserId(userId);
    if (pseudonymizedId && this.gaInitialized) {
      ReactGA.set({
        userId: pseudonymizedId,
        ...properties
      });
    }
  }
}

// Export singleton instance
const analyticsManager = new AnalyticsManager();
export default analyticsManager;
